import type { Probe } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { bucketDimension, sig } from '../engine/normalize';

/** Screen / viewport / media-query-friendly display properties. */
export const displayProbe: Probe = {
  id: 'display',
  label: 'Display',
  layer: 'display',
  tier: 'client',
  category: 'hardware',
  description: 'Screen metrics, viewport, orientation, and pixel density',
  budgetMs: 1000,

  async run() {
    const s = screen;
    const w = window;
    const orientation = (s.orientation?.type ?? null) as string | null;

    // availLeft/availTop are non-standard but present in Chrome/Firefox and
    // reveal multi-monitor layout; a synthetic screen object often omits them.
    const availLeft = (s as Screen & { availLeft?: number }).availLeft ?? null;
    const availTop = (s as Screen & { availTop?: number }).availTop ?? null;
    // isExtended is the modern multi-monitor signal.
    const isExtended = (s as Screen & { isExtended?: boolean }).isExtended ?? null;

    return {
      signals: {
        width: ok(s.width),
        height: ok(s.height),
        availWidth: ok(s.availWidth),
        availHeight: ok(s.availHeight),
        availLeft: ok(availLeft),
        availTop: ok(availTop),
        isExtended: ok(isExtended),
        isFullScreen: ok(document.fullscreenElement !== null, 'client'),
        colorDepth: ok(s.colorDepth),
        pixelDepth: ok(s.pixelDepth),
        devicePixelRatio: ok(sig(w.devicePixelRatio, 3)),
        innerWidth: ok(w.innerWidth),
        innerHeight: ok(w.innerHeight),
        outerWidth: ok(w.outerWidth),
        outerHeight: ok(w.outerHeight),
        screenOrientation: ok(orientation),
        orientationAngle: ok(s.orientation?.angle ?? null),
        // availHeight significantly below height is normal (taskbars), but
        // availHeight > height is not.
        availHeightExceedsScreen: ok(s.availHeight > s.height, 'derived'),
      },
      parts: {
        // Bucket dimensions so a 1px window change doesn't flip the part.
        screen: {
          width: bucketDimension(s.width),
          height: bucketDimension(s.height),
          colorDepth: s.colorDepth,
          pixelDepth: s.pixelDepth,
          dpr: sig(w.devicePixelRatio, 3),
        },
      },
    };
  },
};

registerProbe(displayProbe);
